import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Fetch weather along the current route polyline. Samples ~5 points at
 * even distance intervals and returns their forecast (temp, precip, wind,
 * bucket icon).
 *
 * Returns { weatherPoints: Array, weatherLoading: boolean }.
 * Refetches whenever the polyline first/last point changes.
 */
export function useRouteWeather(polyline, { maxPoints = 5, enabled = true } = {}) {
  const [weatherPoints, setWeatherPoints] = useState([]);
  const [weatherLoading, setWeatherLoading] = useState(false);

  // Dependency signature — refetch only when the polyline shape actually changes
  const polyLen = polyline ? polyline.length : 0;
  const firstLat = polyline?.[0]?.[0];
  const firstLng = polyline?.[0]?.[1];
  const lastLat = polyline?.[polyLen - 1]?.[0];
  const lastLng = polyline?.[polyLen - 1]?.[1];

  useEffect(() => {
    if (!enabled || !polyline || polyline.length < 2) {
      setWeatherPoints([]);
      return;
    }
    let cancelled = false;
    setWeatherLoading(true);
    (async () => {
      try {
        const step = Math.max(1, Math.floor(polyline.length / 200));
        const trimmed = polyline.filter((_, i) => i % step === 0);
        if (trimmed[trimmed.length - 1] !== polyline[polyline.length - 1]) {
          trimmed.push(polyline[polyline.length - 1]);
        }
        const { data } = await api.post("/routes/weather", {
          polyline: trimmed,
          max_points: maxPoints,
        });
        if (!cancelled) setWeatherPoints(data?.points || []);
      } catch {
        if (!cancelled) setWeatherPoints([]);
      } finally {
        if (!cancelled) setWeatherLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polyLen, firstLat, firstLng, lastLat, lastLng, maxPoints, enabled]);

  return { weatherPoints, weatherLoading };
}
